import type { Route } from '../admin-fetch';
import { ADMIN_API, badRequest, bodyOf, call, failure, seg, toRawResponse, toResponse } from '../respond';

// System categories, CMS pages and the app-update rules. The server owns
// the rules for all three; these only translate addresses and shapes.

const CATEGORIES = `${ADMIN_API}/categories`;
const PAGES = `${ADMIN_API}/pages`;
const APP_VERSION = `${ADMIN_API}/app-version`;

export const catalogRoutes: Route[] = [
  // ── categories
  ['GET', '/api/categories', async () => toResponse(await call('GET', CATEGORIES), 'categories')],
  ['POST', '/api/categories', async (req) =>
    toResponse(await call('POST', CATEGORIES, await bodyOf(req)), 'category', 201)],
  ['PATCH', '/api/categories', async (req) => {
    const { id, ...changes } = await bodyOf<{ id?: string }>(req);
    if (!id) return badRequest('ID required');
    return toResponse(await call('PATCH', `${CATEGORIES}/${seg(id)}`, changes), 'category');
  }],
  ['DELETE', '/api/categories', async (req) => {
    const { id } = await bodyOf<{ id?: string }>(req);
    if (!id) return badRequest('ID required');
    const result = await call('DELETE', `${CATEGORIES}/${seg(id)}`);
    return result.ok ? Response.json({ success: true }) : failure(result);
  }],

  // ── CMS pages
  ['GET', '/api/pages', async (req) => {
    const slug = new URL(req.url).searchParams.get('slug');
    if (slug) return toResponse(await call('GET', `${PAGES}/${seg(slug)}`), 'page');
    return toResponse(await call('GET', PAGES), 'pages');
  }],
  ['PUT', '/api/pages', async (req) => {
    const { slug, title, content } = await bodyOf<{ slug?: string; title?: string; content?: string }>(req);
    if (!slug) return badRequest('Slug required');
    return toResponse(await call('PUT', `${PAGES}/${seg(slug)}`, { title, content }), 'page');
  }],

  // ── app-update rules
  ['GET', '/api/app-version', async (req) => {
    const params = new URL(req.url).searchParams;
    const platform = params.get('platform');
    const currentVersion = params.get('currentVersion');
    if (platform && currentVersion) {
      const query = new URLSearchParams({ platform, currentVersion });
      return toRawResponse(await call('GET', `${APP_VERSION}/resolve?${query}`));
    }
    return toResponse(await call('GET', APP_VERSION), 'configs');
  }],
  ['PUT', '/api/app-version', async (req) =>
    toResponse(await call('PUT', APP_VERSION, await bodyOf(req)), 'config')],
  ['GET', '/api/app-version/history', async (req) => {
    const platform = new URL(req.url).searchParams.get('platform');
    if (!platform || !['android', 'ios'].includes(platform)) return badRequest('Invalid platform');
    return toResponse(await call('GET', `${APP_VERSION}/history?platform=${platform}`), 'history');
  }],
  ['POST', '/api/app-version/rollback', async (req) => {
    const { historyId } = await bodyOf<{ historyId?: string }>(req);
    if (!historyId) return badRequest('historyId required');
    return toResponse(await call('POST', `${APP_VERSION}/rollback`, { historyId }), 'config');
  }],
];
